/**
 * Phase 27.39 — Team (non-agent staff) directory page.
 *
 * A KR-Admin curated list of every user who is NOT an insurance agent
 * — KR SuperAdmins, KR Admins, Accounting, Managers (staff-only), and
 * Agency/Sub-Agency Admins. Two entry points into the roster:
 *
 *   1. "Send Invite" → email invitation with a set-password link.
 *   2. "Add Manually" → KR creates the account with a temporary
 *      password shown once so it can be delivered out-of-band.
 *
 * Non-KR viewers see the button set hidden and can only click into
 * their own row or downline members if they are an Agency Admin.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Users, Search, Plus, Mail, Loader2, Copy, CheckCircle,
  Shield, ShieldAlert, User as UserIcon, Building2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, getErrorMessage, formatPhone } from '@/lib/utils';
import { teamAPI, agencyAPI } from '@/lib/api';
import { UserAvatar } from '@/components/users/UserAvatar';
import { TeamCalendarWidget } from '@/components/team/TeamCalendarWidget';

const ROLE_OPTIONS = [
  { value: 'KR_SuperAdmin', label: 'KR Super Admin' },
  { value: 'KR_AdminOps', label: 'KR Admin' },
  { value: 'Accounting', label: 'Accounting' },
  { value: 'Agency_User', label: 'Agency Admin / Sub-Agency Admin' },
];

const ROLE_LABEL = {
  KR_SuperAdmin: 'KR Super Admin',
  KR_AdminOps: 'KR Admin',
  Accounting: 'Accounting',
  Agency_User: 'Agency Admin',
  Agency_Principal: 'Agency Principal',
};

const ROLE_TONE = {
  KR_SuperAdmin: 'bg-rose-100 text-rose-800 border-rose-200',
  KR_AdminOps: 'bg-orange-100 text-orange-800 border-orange-200',
  Accounting: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Agency_User: 'bg-sky-100 text-sky-800 border-sky-200',
  Agency_Principal: 'bg-violet-100 text-violet-800 border-violet-200',
};

function RoleBadge({ role, agencyName }) {
  const tone = ROLE_TONE[role] || 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <Badge variant="outline" className={`text-[10px] ${tone}`}>
      <Shield className="w-3 h-3 mr-1" />
      {ROLE_LABEL[role] || role}
      {agencyName && <span className="ml-1 opacity-70">· {agencyName}</span>}
    </Badge>
  );
}

function InviteOrAddDialog({ open, onOpenChange, mode, onSuccess }) {
  const isInvite = mode === 'invite';
  const [saving, setSaving] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', role: '', agency_id: '',
  });
  const [manualResult, setManualResult] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ first_name: '', last_name: '', email: '', role: '', agency_id: '' });
      setManualResult(null);
      agencyAPI.list()
        .then((r) => setAgencies(r.data || []))
        .catch(() => setAgencies([]));
    }
  }, [open]);

  const roleRequiresAgency = form.role === 'Agency_User';

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.role) {
      toast.error('Fill in first name, last name, email and role');
      return;
    }
    if (roleRequiresAgency && !form.agency_id) {
      toast.error('Select an agency for this role');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        role: form.role,
        agency_id: roleRequiresAgency ? form.agency_id : null,
      };
      if (isInvite) {
        await teamAPI.sendInvite(payload);
        toast.success('Invitation email sent');
        onSuccess?.();
        onOpenChange(false);
      } else {
        const r = await teamAPI.createManual(payload);
        setManualResult(r.data);
        toast.success('Member added');
        onSuccess?.();
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={`team-${mode}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isInvite ? 'Invite Team Member' : 'Add Member Manually'}
          </DialogTitle>
        </DialogHeader>
        {manualResult ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              <div className="flex items-center gap-2 font-semibold mb-2">
                <CheckCircle className="w-4 h-4" />
                Account created
              </div>
              <p className="text-xs">
                Share the temporary password with {manualResult.display_name}.
                They will be prompted to change it on first login.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <Label className="text-xs text-slate-500 uppercase">Temporary Password</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 px-2 py-1 rounded bg-white border border-slate-200 font-mono text-sm">
                  {manualResult.temporary_password}
                </code>
                <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(manualResult.temporary_password);
                    toast.success('Copied');
                  }}
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                  required
                  data-testid="team-invite-first-name"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                  required
                  data-testid="team-invite-last-name"
                />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                required
                data-testid="team-invite-email"
              />
            </div>
            <div>
              <Label>Role *</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm((p) => ({ ...p, role: v, agency_id: '' }))}
              >
                <SelectTrigger data-testid="team-invite-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {roleRequiresAgency && (
              <div>
                <Label>Agency *</Label>
                <Select
                  value={form.agency_id}
                  onValueChange={(v) => setForm((p) => ({ ...p, agency_id: v }))}
                >
                  <SelectTrigger data-testid="team-invite-agency">
                    <SelectValue placeholder="Select an agency" />
                  </SelectTrigger>
                  <SelectContent>
                    {agencies.map((a) => (
                      <SelectItem key={a.agency_id} value={a.agency_id}>
                        {a.agency_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isInvite ? (
              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2">
                <Mail className="inline w-3 h-3 mr-1" />
                An invitation email will be sent with a link to set their password.
              </p>
            ) : (
              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2">
                <ShieldAlert className="inline w-3 h-3 mr-1" />
                The account will be created with a temporary password shown
                only once. Share it via a secure channel.
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid={`team-${mode}-submit`}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isInvite ? 'Send invitation' : 'Add member'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function TeamPage() {
  const { user } = useAuth();
  const isKR = isKRRole(user);

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [dialogMode, setDialogMode] = useState(null); // 'invite' | 'manual'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (roleFilter !== 'all') params.role = roleFilter;
      const r = await teamAPI.list(params);
      setMembers(r.data?.items || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load team'));
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    members.forEach((m) => {
      const primary = m.roles?.[0]?.role || 'Other';
      if (!g[primary]) g[primary] = [];
      g[primary].push(m);
    });
    return g;
  }, [members]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Team"
        description="Team accounts that are not agents — KR Admins, Accounting, Managers, and Agency Admins."
        actions={
          isKR && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setDialogMode('manual')}
                data-testid="team-add-manual-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
              <Button
                onClick={() => setDialogMode('invite')}
                data-testid="team-send-invite-btn"
              >
                <Mail className="w-4 h-4 mr-1" /> Send Invite
              </Button>
            </div>
          )
        }
      />

      <div className="mt-6 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-9"
            data-testid="team-search"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-64" data-testid="team-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="animate-pulse flex gap-3">
                  <div className="w-10 h-10 bg-slate-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-100 rounded w-1/2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-lg bg-slate-50">
          <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h3 className="text-base font-semibold text-slate-700">
            No team members yet
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            {isKR
              ? 'Add your first member to start managing the team.'
              : 'No team accounts have been created yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([role, list]) => (
            <div key={role}>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                {ROLE_LABEL[role] || role} ({list.length})
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((m) => (
                  <Link
                    key={m.user_id}
                    to={`/team/${m.user_id}`}
                    className="group"
                    data-testid={`team-member-row-${m.user_id}`}
                  >
                    <Card className="hover:shadow-md hover:border-sky-200 transition-all">
                      <CardContent className="p-4">
                        <div className="flex gap-3">
                          <UserAvatar user={m} size="w-11 h-11" testId={`team-avatar-${m.user_id}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="font-semibold text-sm text-slate-900 truncate">
                                {m.display_name}
                              </h3>
                              {m.status !== 'active' && (
                                <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-600">
                                  {m.status}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 truncate">{m.email}</p>
                            {m.phone && (
                              <p className="text-xs text-slate-400 truncate">{formatPhone(m.phone)}</p>
                            )}
                            <div className="flex flex-wrap gap-1 mt-2">
                              {(m.roles || []).map((r, i) => (
                                <RoleBadge key={i} role={r.role} agencyName={r.agency_name} />
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <InviteOrAddDialog
        open={dialogMode !== null}
        onOpenChange={(o) => !o && setDialogMode(null)}
        mode={dialogMode || 'invite'}
        onSuccess={load}
      />

      {/* Phase 27.39.2 — Unified Google Calendar view (KR only). */}
      {isKR && (
        <div className="mt-8">
          <TeamCalendarWidget />
        </div>
      )}
    </div>
  );
}
